import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { 
  Building2, FileText, Settings, LogOut, Check, Crown, BarChart3,
  Loader2, Mail, MessageSquare, HelpCircle, Calculator, AlertCircle,
  Send, Phone, Clock
} from "lucide-react";
import { toast } from "sonner";

const SupportPage = ({ auth }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [supportInfo, setSupportInfo] = useState(null);
  const [formData, setFormData] = useState({
    subject: "",
    message: "",
    email_type: "support"
  });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    fetchSupportInfo();
  }, []);

  const fetchSupportInfo = async () => {
    try {
      const response = await axios.get(`${API}/support/info`);
      setSupportInfo(response.data);
    } catch (error) {
      console.error("Failed to load support info");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.subject.trim() || !formData.message.trim()) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/support/contact`, formData, {
        withCredentials: true
      });
      setSubmitted(true);
      toast.success("Support request submitted successfully!");
      setFormData({ subject: "", message: "", email_type: "support" });
    } catch (error) {
      toast.error("Failed to submit request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const tierBadgeColor = {
    trial: "bg-slate-100 text-slate-700",
    basic: "bg-teal-100 text-teal-700",
    pro: "bg-purple-100 text-purple-700",
    admin: "bg-amber-100 text-amber-700"
  };

  const currentTier = auth.user?.subscription_tier || "trial";

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-navy-900 text-white hidden lg:block">
        <div className="p-6">
          <Link to="/" className="flex items-center space-x-2">
            <img src="https://customer-assets.emergentagent.com/job_property-analyzer-23/artifacts/1qqdoef6_Untitled_design-removebg-preview.png" alt="Rentlytix" className="h-12 w-auto" />
            
          </Link>
        </div>
        
        <nav className="px-4 space-y-1">
          <Link 
            to="/dashboard" 
            className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors"
          >
            <BarChart3 className="h-5 w-5" />
            <span>Dashboard</span>
          </Link>
          <Link 
            to="/reports" 
            className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors"
          >
            <FileText className="h-5 w-5" />
            <span>My Reports</span>
          </Link>
          <Link 
            to="/calculators" 
            className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors"
          >
            <Calculator className="h-5 w-5" />
            <span>Deal Calculators</span>
          </Link>
          <Link 
            to="/compare" 
            className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors"
          >
            <Calculator className="h-5 w-5" />
            <span>Compare</span>
            {currentTier !== "pro" && currentTier !== "admin" && (
              <Badge className="bg-purple-500 text-white text-xs ml-auto">PRO</Badge>
            )}
          </Link>
          <Link 
            to="/subscription" 
            className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors"
          >
            <Crown className="h-5 w-5" />
            <span>Subscription</span>
          </Link>
          <Link 
            to="/support" 
            className="flex items-center space-x-3 px-4 py-3 rounded-lg bg-white/10 text-white"
          >
            <HelpCircle className="h-5 w-5" />
            <span>Support</span>
          </Link>
          <Link 
            to="/settings" 
            className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors"
          >
            <Settings className="h-5 w-5" />
            <span>Settings</span>
          </Link>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4">
          <div className="bg-white/10 rounded-lg p-4">
            <div className="flex items-center space-x-3 mb-3">
              <Avatar className="h-10 w-10">
                <AvatarImage src={auth.user?.picture} />
                <AvatarFallback className="bg-teal-500 text-navy-900">
                  {auth.user?.name?.charAt(0) || "U"}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{auth.user?.name}</p>
                <Badge className={`${tierBadgeColor[currentTier] || tierBadgeColor.basic} text-xs`}>
                  {currentTier.toUpperCase()}
                </Badge>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              className="w-full justify-start text-slate-300 hover:text-white hover:bg-white/10"
              onClick={auth.logout}
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200">
        <div className="flex items-center justify-between px-4 h-16">
          <Link to="/" className="flex items-center space-x-2">
            <img src="https://customer-assets.emergentagent.com/job_property-analyzer-23/artifacts/1qqdoef6_Untitled_design-removebg-preview.png" alt="Rentlytix" className="h-12 w-auto" />
            
          </Link>
          <div className="flex items-center space-x-2">
            <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
              <BarChart3 className="h-5 w-5" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => navigate("/settings")}>
              <Settings className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0">
        <div className="p-6 lg:p-8 max-w-4xl">
          {/* Header */}
          <div className="mb-8">
            <h1 className="font-heading text-2xl lg:text-3xl font-bold text-navy-900 mb-2">
              Help & Support
            </h1>
            <p className="text-slate-600">
              Get help with your account, report issues, or ask questions
            </p>
          </div>

          {/* Contact Info Cards */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <Card className="border-teal-200 bg-teal-50/50">
              <CardHeader>
                <div className="flex items-center space-x-3">
                  <div className="p-2 rounded-lg bg-teal-500">
                    <Mail className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Technical Support</CardTitle>
                    <CardDescription>For issues and billing questions</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <a 
                  href={`mailto:${supportInfo?.support_email || 'support@rentlytix.com'}`}
                  className="text-teal-600 hover:text-teal-700 font-medium flex items-center"
                >
                  {supportInfo?.support_email || 'support@rentlytix.com'}
                </a>
                <p className="text-sm text-slate-600 mt-2">
                  {supportInfo?.support_description || 'For technical issues, billing questions, and subscription cancellations'}
                </p>
              </CardContent>
            </Card>

            <Card className="border-purple-200 bg-purple-50/50">
              <CardHeader>
                <div className="flex items-center space-x-3">
                  <div className="p-2 rounded-lg bg-purple-500">
                    <MessageSquare className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Questions & Features</CardTitle>
                    <CardDescription>Product questions and suggestions</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <a 
                  href={`mailto:${supportInfo?.info_email || 'info@rentlytix.com'}`}
                  className="text-purple-600 hover:text-purple-700 font-medium flex items-center"
                >
                  {supportInfo?.info_email || 'info@rentlytix.com'}
                </a>
                <p className="text-sm text-slate-600 mt-2">
                  {supportInfo?.info_description || 'For product questions, feature requests, and general inquiries'}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          {submitted ? (
            <Card className="border-teal-200 bg-teal-50">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-teal-500 flex items-center justify-center mx-auto mb-4">
                  <Check className="h-8 w-8 text-white" />
                </div>
                <h3 className="font-heading text-xl font-bold text-navy-900 mb-2">
                  Request Submitted!
                </h3>
                <p className="text-slate-600 mb-6">
                  We've received your message and will get back to you within 24 hours.
                </p>
                <Button onClick={() => setSubmitted(false)} variant="outline">
                  Submit Another Request
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="font-heading flex items-center">
                  <Send className="h-5 w-5 mr-2 text-teal-500" />
                  Contact Us
                </CardTitle>
                <CardDescription>
                  Fill out the form below and we'll get back to you as soon as possible
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-3">
                    <Label>Request Type</Label>
                    <RadioGroup
                      value={formData.email_type}
                      onValueChange={(value) => setFormData({ ...formData, email_type: value })}
                      className="flex space-x-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="support" id="support" />
                        <Label htmlFor="support" className="cursor-pointer">
                          Technical Support / Billing
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="info" id="info" />
                        <Label htmlFor="info" className="cursor-pointer">
                          Questions / Feature Requests
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subject">Subject</Label>
                    <Input
                      id="subject"
                      placeholder="Brief description of your request"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Message</Label>
                    <Textarea
                      id="message"
                      placeholder="Please provide details about your request or issue..."
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      rows={6}
                      required
                    />
                  </div>

                  <Button type="submit" disabled={loading} className="w-full bg-teal-500 hover:bg-teal-400 text-navy-900">
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Send className="h-4 w-4 mr-2" />
                    )}
                    {loading ? "Submitting..." : "Submit Request"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}

          {/* FAQ Section */}
          <Card className="mt-8">
            <CardHeader>
              <CardTitle className="font-heading flex items-center">
                <HelpCircle className="h-5 w-5 mr-2 text-slate-500" />
                Frequently Asked Questions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h4 className="font-medium text-navy-900 mb-2">How do I cancel my subscription?</h4>
                <p className="text-sm text-slate-600">
                  You can cancel your subscription anytime from the Subscription page. Your access will continue until the end of your billing period. 
                  Alternatively, contact us at support@rentlytix.com.
                </p>
              </div>
              <div>
                <h4 className="font-medium text-navy-900 mb-2">What happens to my reports if I cancel?</h4>
                <p className="text-sm text-slate-600">
                  Your existing reports will remain accessible even after cancellation. You just won't be able to generate new reports once your subscription ends.
                </p>
              </div>
              <div>
                <h4 className="font-medium text-navy-900 mb-2">How long does it take to get a response?</h4>
                <p className="text-sm text-slate-600">
                  We typically respond to support requests within 24 hours during business days. Pro subscribers receive priority support.
                </p>
              </div>
              <div>
                <h4 className="font-medium text-navy-900 mb-2">What if I exceed my report limit?</h4>
                <p className="text-sm text-slate-600">
                  Reports beyond your monthly/annual limit are charged at $0.15 per report. You can also upgrade your plan anytime for more reports.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Response Time Note */}
          <div className="mt-6 flex items-center justify-center text-sm text-slate-500">
            <Clock className="h-4 w-4 mr-2" />
            <span>Average response time: 24 hours</span>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SupportPage;
